import { ref, watch } from '@vue/composition-api';
import { useRouter } from '@core/utils';

/**
 * Composable to sync a reactive value with URL query parameters.
 * Removes the query param from the URL when the value equals the default.
 *
 * @param {string} key - The query parameter key
 * @param {string|number} defaultValue - The default value if not present in URL
 * @returns {import('@vue/composition-api').Ref} A ref that syncs with the URL query parameter
 *
 * @example
 * const tab = useUrlQuery('tab', 0)
 * const search = useUrlQuery('search', '')
 */
export function useUrlQuery(key, defaultValue) {
    const { route, router } = useRouter();

    // Parse the raw query string value back to the correct type
    const parseValue = (queryValue) => {
        if (queryValue === null || queryValue === undefined || queryValue === '') {
            return defaultValue;
        }

        const stringValue = String(queryValue);

        if (typeof defaultValue === 'number') {
            const parsed = parseInt(stringValue, 10);
            return isNaN(parsed) ? defaultValue : parsed;
        }

        return stringValue;
    };

    // Initialize from URL or fall back to default
    const value = ref(parseValue(route.value.query[key]));

    // Sync URL when the value changes
    watch(value, (newValue) => {
        const newQuery = { ...route.value.query };

        if (newValue === defaultValue) {
            delete newQuery[key];
        } else {
            newQuery[key] = String(newValue);
        }

        router.replace({ query: newQuery });
    });

    // Update local value when URL changes (e.g. browser back/forward)
    watch(
        () => route.value.query[key],
        (newQueryValue) => {
            const parsed = parseValue(newQueryValue);
            if (parsed !== value.value) {
                value.value = parsed;
            }
        }
    );

    return value;
}
